import React, { useMemo, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import useFilters from '@ubeya/shared/hooks/account/useFilters';
import useFields from '@ubeya/shared/hooks/account/useFields';
import { formatOptions, mergeExtraFilters, removeFilter } from '../../utils/filters';
import { FlexMiddle, FlexColumn } from '../Flex';
import DropDown from '../DropDown';
import FilterOptions from '../Filter/FilterOptions';
import ConfigContext from '../../contexts/ConfigContext';
import {
  GENERAL_FILTERS_SLUGS,
  HELP_DESK_FILTERS_SLUGS,
  HIRING_FILTERS_SLUGS,
  LIVE_VIEW_FILTERS_SLUGS,
  POSITIONS_SETTINGS_FILTERS_SLUGS,
  PROJECT_POST_FILTERS_SLUGS,
  PROJECT_SCHEDULING_FILTERS_SLUGS
} from '../../constants';
import Chip from './Chip';
import PositionChip from './CustomChips/PositionsChip';
import RatingChip from './CustomChips/RatingChip';
import EmployeeTypeChip from './CustomChips/EmployeeTypeChip';
import AdminChip from './CustomChips/AdminChip';
import TagsChip from './CustomChips/TagsChip';
import ClientsChip from './CustomChips/ClientsChip';
import AgenciesChip from './CustomChips/AgenciesChip';
import BranchesChip from './CustomChips/BranchesChip';
import RestrictedClientChip from './CustomChips/RestrictedClientChip';
import BookedInProjectChip from './CustomChips/BookedInProjectChip';
import SuppliersByBranchChip from './CustomChips/SuppliersByBranchChip';
import LocationsChip from './CustomChips/LocationsChip';
import AreasChip from './CustomChips/AreasChip';
import FieldChip from './CustomChips/FieldChip';
import AgeChip from './CustomChips/AgeChip';
import DateChip from './CustomChips/DateChip';
import PreferredClientsChip from './CustomChips/PreferredClientsChip';
import EventBookerChip from './CustomChips/EventBookerChip';
import LiveViewEmployeeTypeChip from './CustomChips/LiveViewEmployeeTypeChip';
import PositionsSettingsChip from './CustomChips/PositionsSettingsChip';
import WorkedWithClientFilterChip from './CustomChips/WorkedWithClientFilterChip';
import BookingStatusChip from './CustomChips/BookingStatusChip';
import BooleanChip from './CustomChips/BooleanChip';
import OrderStatusChip from './CustomChips/OrderStatusChip';
import StartTimeChip from './CustomChips/StartTimeChip';
import CostCenterChip from './CustomChips/CostCenterChip';
import SuppliersByAccountChip from './CustomChips/SuppliersByAccountChip';
import ProjectBookingStatusChip from './CustomChips/ProjectBookingStatusChip';
import ProjectBookingIssuesStatusChip from './CustomChips/ProjectBookingIssuesStatusChip';
import PayrollProcessStatusChip from './CustomChips/PayrollProcessStatusChip';
import AgeWarningChip from './CustomChips/AgeWarningChip';
import ProjectLiveViewStatusChip from './CustomChips/ProjectLiveViewStatusChip';
import DepartmentChip from './CustomChips/DepartmentChip';
import EmploymentTypeChip from './CustomChips/EmploymentTypeChip';
import ProjectCostCenterChip from './CustomChips/ProjectCostCenterChip';
import WorkedInAreaChip from './CustomChips/WorkedInAreaChip';
import PreferredAreaChip from './CustomChips/PreferredAreaChip';
import AccountRoleChip from './CustomChips/AccountRoleChip';
import PublishFeedOptionsChip from './CustomChips/PublishFeedOptionsChip';
import ContractsChip from './CustomChips/ContractsChip';
import LeaveContractsChip from './CustomChips/LeaveContractsChip';
import MainEventChip from './CustomChips/MainEventChip';
import HelpDeskCategoriesChip from './CustomChips/HelpDeskCategoriesChip';
import HelpDeskAssigneesChip from './CustomChips/HelpDeskAssigneesChip';
import HelpDeskPriorityChip from './CustomChips/HelpDeskPriorityChip';
import ShiftTypeChip from './CustomChips/ShiftTypeChip';
import ClockSourceChip from './CustomChips/ClockSourceChip';
import ExternalOptionsChip from './CustomChips/ExternalOptionsChip';
import OptionsChip from './CustomChips/OptionsChip';

const COMPONENTS_MAP = {
  positions: PositionChip,
  rating: RatingChip,
  employeeType: EmployeeTypeChip,
  workedWithClient: WorkedWithClientFilterChip,
  adminType: AdminChip,
  tags: TagsChip,
  eventBooker: EventBookerChip,
  clients: ClientsChip,
  [GENERAL_FILTERS_SLUGS.AGENCIES]: AgenciesChip,
  suppliers: SuppliersByBranchChip,
  branches: BranchesChip,
  locations: LocationsChip,
  department: DepartmentChip,
  employmentType: EmploymentTypeChip,
  workedInArea: WorkedInAreaChip,
  preferredArea: PreferredAreaChip,
  adminRole: AccountRoleChip,
  restrictedClient: RestrictedClientChip,
  bookedInProjects: BookedInProjectChip,
  payrollProcessStatus: PayrollProcessStatusChip,
  age: AgeChip,
  hasApp: BooleanChip,
  photoVerified: BooleanChip,
  approved: BooleanChip,
  clientApproval: BooleanChip,
  notBookedOnDate: DateChip,
  preferredClients: PreferredClientsChip,
  bookingStatus: BookingStatusChip,
  availableByTimeAvailability: BooleanChip,
  notExceedingEarningsLimit: BooleanChip,
  orderStatusFilter: OrderStatusChip,
  costCenter: CostCenterChip,
  mainEvent: MainEventChip,
  shiftTypes: ShiftTypeChip,
  [GENERAL_FILTERS_SLUGS.CLOCK_SOURCE]: ClockSourceChip,

  [PROJECT_POST_FILTERS_SLUGS.positions]: PositionChip,
  [PROJECT_POST_FILTERS_SLUGS.locations]: LocationsChip,
  [PROJECT_POST_FILTERS_SLUGS.dates]: DateChip,
  [PROJECT_POST_FILTERS_SLUGS.audience]: PublishFeedOptionsChip,

  [PROJECT_SCHEDULING_FILTERS_SLUGS.positions]: PositionChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.locations]: LocationsChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.mainLocations]: LocationsChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.areas]: AreasChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.startTime]: StartTimeChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.endTime]: StartTimeChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.suppliers]: SuppliersByAccountChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.branchCostCenters]: ProjectCostCenterChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingStatus]: ProjectBookingStatusChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.bookingIssues]: ProjectBookingIssuesStatusChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.ageWarning]: AgeWarningChip,
  [PROJECT_SCHEDULING_FILTERS_SLUGS.liveViewStatus]: ProjectLiveViewStatusChip,

  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_IN]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CLOCK_OUT]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_IN]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_CHECK_OUT]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.WITH_BREAKS]: BooleanChip,
  [GENERAL_FILTERS_SLUGS.CONTRACT]: ContractsChip,
  [GENERAL_FILTERS_SLUGS.LEAVE_CONTRACT]: LeaveContractsChip,

  [LIVE_VIEW_FILTERS_SLUGS.costCenter]: ProjectCostCenterChip,
  [LIVE_VIEW_FILTERS_SLUGS.positions]: PositionChip,
  [LIVE_VIEW_FILTERS_SLUGS.locations]: LocationsChip,
  [LIVE_VIEW_FILTERS_SLUGS.areas]: LocationsChip,
  [LIVE_VIEW_FILTERS_SLUGS.clients]: ClientsChip,
  [LIVE_VIEW_FILTERS_SLUGS.startTime]: StartTimeChip,
  [LIVE_VIEW_FILTERS_SLUGS.endTime]: StartTimeChip,
  [LIVE_VIEW_FILTERS_SLUGS.eventBooker]: EventBookerChip,
  [LIVE_VIEW_FILTERS_SLUGS.employeeType]: LiveViewEmployeeTypeChip,

  [POSITIONS_SETTINGS_FILTERS_SLUGS.branches]: PositionsSettingsChip,
  [POSITIONS_SETTINGS_FILTERS_SLUGS.compliance]: PositionsSettingsChip,

  [HELP_DESK_FILTERS_SLUGS.CATEGORIES]: HelpDeskCategoriesChip,
  [HELP_DESK_FILTERS_SLUGS.ASSIGNEES]: HelpDeskAssigneesChip,
  [HELP_DESK_FILTERS_SLUGS.PRIORITY]: HelpDeskPriorityChip,

  // Options come from the page's externalOptions, not useBranches/usePositions
  [HIRING_FILTERS_SLUGS.BRANCHES]: ExternalOptionsChip,
  [HIRING_FILTERS_SLUGS.POSITIONS]: ExternalOptionsChip,
  [HIRING_FILTERS_SLUGS.STATUS]: ExternalOptionsChip
};

const getChipComponent = ({ type, slug, options }) => {
  if (options) return OptionsChip;
  return type === 'basic' ? COMPONENTS_MAP[slug] : FieldChip;
};

const Wrapper = styled(FlexMiddle).attrs(() => ({ className: 'filters-row-wrapper' }))`
  flex-wrap: wrap;
  margin-top: 10px;
  margin-bottom: -10px;
`;

const TextList = styled(FlexColumn)`
  gap: 8px;
`;

const TextRow = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.black};
`;

const TextLabel = styled.div`
  color: ${({ theme }) => theme.colors.grey4};
`;

const FiltersRow = ({
  filterContextParams,
  selectedKey,
  filters,
  setFilters,
  disabledFilters = [],
  disableAllFilters = false,
  usePortal,
  className,
  externalOptions,
  extraFilters,
  portalStyle,
  // 'chips' (default) = interactive removable pills. 'text' = plain read-only summary
  layout = 'chips'
}) => {
  const { t } = useContext(ConfigContext);
  const { filters: data } = useFilters();
  const { mappedFields } = useFields();

  const options = useMemo(() => {
    const selectedKeyData = mergeExtraFilters(data[selectedKey], extraFilters);
    const basicOptions = formatOptions({ options: selectedKeyData, type: 'basic', filters });

    const fieldsOptions = formatOptions({
      options: selectedKeyData,
      type: 'fields',
      formatSlug: (id) => mappedFields?.[id]?.name,
      filters
    });

    return [...basicOptions, ...fieldsOptions].filter(({ value }) => Number.isInteger(value) || !isEmpty(value));
  }, [data, extraFilters, filters, mappedFields, selectedKey]);

  const handleRemoveFilter = useCallback(
    ({ type, id }) => {
      setFilters(removeFilter({ type, id, allFilters: filters }));
    },
    [filters, setFilters]
  );

  if (layout === 'text') {
    return (
      <TextList className={className}>
        {options.map(({ slug, value, type, id, name, options: filterOptions }, index) => {
          const Component = getChipComponent({ type, slug, options: filterOptions });
          return (
            <TextRow key={index}>
              <TextLabel>{t(name ?? slug)}:</TextLabel>
              {Component ? (
                <Component
                  id={id}
                  slug={slug}
                  value={value}
                  options={filterOptions}
                  externalOptions={externalOptions}
                />
              ) : Array.isArray(value) ? (
                value.join(', ')
              ) : (
                value
              )}
            </TextRow>
          );
        })}
      </TextList>
    );
  }

  return (
    <Wrapper className={className}>
      {options.map(({ slug, value, type, id, name, options: filterOptions }, index) => {
        const isDisabled = disableAllFilters || disabledFilters.includes(slug);
        const Container = isDisabled ? ({ renderValue }) => renderValue : DropDown;
        const Component = getChipComponent({ type, slug, options: filterOptions });
        return (
          <Container
            key={index}
            openFromRight={!usePortal}
            usePortal={usePortal}
            portalStyle={portalStyle}
            showArrow={false}
            renderValue={
              <Chip
                label={t(name ?? slug)}
                value={
                  Component ? (
                    <Component
                      id={id}
                      slug={slug}
                      value={value}
                      options={filterOptions}
                      externalOptions={externalOptions}
                    />
                  ) : Array.isArray(value) ? (
                    value.join(',')
                  ) : (
                    value
                  )
                }
                onRemove={isDisabled ? undefined : () => handleRemoveFilter({ type, id })}
                isDisabled={isDisabled}
              />
            }>
            <FilterOptions
              {...{
                filterContextParams,
                externalOptions,
                extraFilters,
                selectedKey,
                filters,
                setFilters,
                selectedOpenFilter: { type, id, slug, value, name, options: filterOptions }
              }}
            />
          </Container>
        );
      })}
    </Wrapper>
  );
};

export default FiltersRow;
